import React, { useEffect } from 'react';

interface SEOHeadProps {
  title?: string;
  description?: string;
  keywords?: string;
  image?: string;
  url?: string;
  type?: 'website' | 'article';
  author?: string;
  canonical?: string;
  robots?: string;
}

/*
 * La imagen de compartición lleva rotulado «WhatsApp: Próximamente» sobre la
 * burbuja de mensajería; el texto alternativo tiene que decir lo mismo.
 */
const IMAGE_ALT =
  'CitasClinicas.AI: recepción para clínicas dentales por teléfono 24/7. La atención por WhatsApp aparece rotulada como «Próximamente»: todavía no está disponible.';

export const SEOHead: React.FC<SEOHeadProps> = ({
  title = 'CitasClinicas.AI - Recepción inteligente para clínicas dentales',
  description = 'Gestiona citas dentales por teléfono las 24 horas, con confirmaciones y cancelaciones automáticas. WhatsApp y sus recordatorios, próximamente.',
  keywords = 'clínica dental, citas dentales, recepción, teléfono, automatización, pacientes, WhatsApp próximamente',
  image = 'https://clinica.centraldereservas.ai/og.png',
  url = 'https://clinica.centraldereservas.ai',
  type = 'website',
  author = 'CitasClinicas.AI',
  canonical,
  robots = 'index,follow',
}) => {
  const fullUrl = canonical || url;

  useEffect(() => {
    // Update document title
    document.title = title;

    // Update meta tags
    const updateMetaTag = (name: string, content: string) => {
      let meta = document.querySelector(
        `meta[name="${name}"]`
      ) as HTMLMetaElement;
      if (!meta) {
        meta = document.createElement('meta');
        meta.name = name;
        document.head.appendChild(meta);
      }
      meta.content = content;
    };

    const updatePropertyMetaTag = (property: string, content: string) => {
      let meta = document.querySelector(
        `meta[property="${property}"]`
      ) as HTMLMetaElement;
      if (!meta) {
        meta = document.createElement('meta');
        meta.setAttribute('property', property);
        document.head.appendChild(meta);
      }
      meta.content = content;
    };

    const updateCanonical = (href: string) => {
      let link = document.querySelector('link[rel="canonical"]') as HTMLLinkElement;
      if (!link) {
        link = document.createElement('link');
        link.rel = 'canonical';
        document.head.appendChild(link);
      }
      link.href = href;
    };

    // Basic meta tags
    updateMetaTag('description', description);
    updateMetaTag('keywords', keywords);
    updateMetaTag('author', author);
    updateMetaTag('robots', robots);
    updateMetaTag('language', 'Spanish');
    updateMetaTag('revisit-after', '7 days');
    updateCanonical(fullUrl);

    // Open Graph tags
    updatePropertyMetaTag('og:type', type);
    updatePropertyMetaTag('og:url', fullUrl);
    updatePropertyMetaTag('og:title', title);
    updatePropertyMetaTag('og:description', description);
    updatePropertyMetaTag('og:image', image);
    updatePropertyMetaTag('og:image:alt', IMAGE_ALT);
    updatePropertyMetaTag('og:site_name', 'CitasClinicas.AI');
    updatePropertyMetaTag('og:locale', 'es_ES');

    // Twitter tags
    updateMetaTag('twitter:card', 'summary_large_image');
    updateMetaTag('twitter:url', fullUrl);
    updateMetaTag('twitter:title', title);
    updateMetaTag('twitter:description', description);
    updateMetaTag('twitter:image', image);
    updateMetaTag('twitter:image:alt', IMAGE_ALT);

    // Mobile meta tags
    updateMetaTag('theme-color', '#F6F1E9');
    updateMetaTag('apple-mobile-web-app-capable', 'yes');
    updateMetaTag('apple-mobile-web-app-status-bar-style', 'default');
    updateMetaTag('apple-mobile-web-app-title', 'CitasClinicas.AI');

    // Add structured data
    const addStructuredData = (id: string, data: object) => {
      let script = document.getElementById(id) as HTMLScriptElement | null;
      if (!script) {
        script = document.createElement('script');
        script.id = id;
        document.head.appendChild(script);
      }
      script.type = 'application/ld+json';
      script.textContent = JSON.stringify(data);
    };

    // Organization structured data
    addStructuredData('seo-organization-schema', {
      '@context': 'https://schema.org',
      '@type': 'Organization',
      name: 'CitasClinicas.AI',
      url: 'https://clinica.centraldereservas.ai',
      logo: 'https://clinica.centraldereservas.ai/favicon-512.png',
      description: 'Recepción inteligente para clínicas dentales',
      address: {
        '@type': 'PostalAddress',
        addressCountry: 'ES',
      },
      contactPoint: {
        '@type': 'ContactPoint',
        contactType: 'customer service',
        availableLanguage: 'Spanish',
      },
    });

    // Service structured data
    addStructuredData('seo-service-schema', {
      '@context': 'https://schema.org',
      '@type': 'Service',
      name: 'Gestión de citas para clínicas dentales',
      provider: {
        '@type': 'Organization',
        name: 'CitasClinicas.AI',
      },
      description:
        'Recepción automatizada para gestionar citas dentales por teléfono. El canal de WhatsApp estará disponible próximamente.',
      areaServed: 'Spain',
      serviceType: 'Software as a Service',
    });

  }, [
    title,
    description,
    keywords,
    author,
    image,
    url,
    type,
    canonical,
    robots,
    fullUrl,
  ]);

  return null;
};
